/**
 * Expenses Page - Monthly Expenses Calculator
 * Calculate savings capacity based on income and expenses
 */

import { storage, requireAuth, requireProfile } from '../utils/storage.js';
import { expensesApi, profileApi } from '../services/api.js';
import { renderNavbar } from '../components/navbar.js';
import { renderFooter } from '../components/footer.js';
import {
  calculateMonthlySavings,
  formatCurrency,
  formatPercentage,
  getSavingsHealth,
  calculateMonthsToSave
} from '../utils/calculations.js';

export async function renderExpenses(container) {
  if (!requireAuth()) return;
  if (!requireProfile()) return;

  const profile = storage.getProfile();
  let savedExpenses = storage.getExpenses();

  // If no local expenses, try to load from backend
  if (!savedExpenses) {
    try {
      const backendExpenses = await expensesApi.getExpenses();
      if (backendExpenses && (backendExpenses.rent || backendExpenses.fixedExpenses || backendExpenses.variableExpenses)) {
        savedExpenses = {
          rent: backendExpenses.rent || 0,
          fixed: backendExpenses.fixedExpenses || 0,
          variable: backendExpenses.variableExpenses || 0
        };
        storage.setExpenses(savedExpenses);
      }
    } catch (e) {
      console.warn('Could not load expenses from backend:', e);
    }
  }

  container.innerHTML = `
    ${renderNavbar()}
    
    <main class="page page-background" style="padding-top: 100px;">
      <div class="container">
        <div style="max-width: 900px; margin: 0 auto;">
          
          <!-- Header -->
          <div style="text-align: center; margin-bottom: 40px;">
            <div style="width: 64px; height: 64px; background: var(--primary-100); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px;">
              <i data-feather="sliders" style="color: var(--primary-600); width: 28px; height: 28px;"></i>
            </div>
            <h1 style="margin-bottom: 8px;">Calculadora de Gastos Mensuales</h1>
            <p style="color: var(--gray-500); max-width: 500px; margin: 0 auto;">
              Conoce tu capacidad real de ahorro y cuánto tiempo necesitas para llegar a tu meta.
            </p>
          </div>
          
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 32px;">
            <!-- Form -->
            <div class="card animate-slideUp">
              <h3 style="margin-bottom: 24px; display: flex; align-items: center; gap: 8px;">
                <i data-feather="edit-3" style="color: var(--primary-600);"></i>
                Tus gastos mensuales
              </h3>
              
              <form id="expensesForm">
                <div class="form-group">
                  <label class="form-label" for="income">
                    Ingresos netos mensuales (€)
                  </label>
                  <input 
                    type="number" 
                    id="income" 
                    class="form-input" 
                    value="${profile.monthlyIncome}"
                    required
                    min="0"
                  />
                </div>
                
                <hr style="border: none; border-top: 1px solid var(--gray-100); margin: 24px 0;">
                
                <div class="form-group">
                  <label class="form-label" for="rent">
                    <span style="display: flex; align-items: center; gap: 6px;">
                      <span style="font-size: 16px;">🏠</span>
                      Alquiler / Hipoteca actual (€)
                    </span>
                  </label>
                  <input 
                    type="number" 
                    id="rent" 
                    class="form-input" 
                    placeholder="Ej: 800"
                    value="${savedExpenses?.rent || ''}"
                    min="0"
                  />
                </div>
                
                <div class="form-group">
                  <label class="form-label" for="fixed">
                    <span style="display: flex; align-items: center; gap: 6px;">
                      <span style="font-size: 16px;">📋</span>
                      Gastos fijos (€)
                    </span>
                  </label>
                  <input 
                    type="number" 
                    id="fixed" 
                    class="form-input" 
                    placeholder="Ej: 300"
                    value="${savedExpenses?.fixed || ''}"
                    min="0"
                  />
                  <small style="color: var(--gray-500); font-size: 12px;">
                    Suministros, seguros, suscripciones, transporte, etc.
                  </small>
                </div>
                
                <div class="form-group">
                  <label class="form-label" for="variable">
                    <span style="display: flex; align-items: center; gap: 6px;">
                      <span style="font-size: 16px;">🛒</span>
                      Gastos variables (€)
                    </span>
                  </label>
                  <input 
                    type="number" 
                    id="variable" 
                    class="form-input" 
                    placeholder="Ej: 400"
                    value="${savedExpenses?.variable || ''}"
                    min="0"
                  />
                  <small style="color: var(--gray-500); font-size: 12px;">
                    Alimentación, ocio, compras, imprevistos, etc.
                  </small>
                </div>
                
                <button type="submit" class="btn btn-primary btn-lg w-full" style="margin-top: 16px;">
                  <i data-feather="save"></i>
                  Calcular y guardar
                </button>
              </form>
            </div>
            
            <!-- Results -->
            <div id="resultsPanel">
              <div class="card animate-slideUp" style="background: linear-gradient(135deg, var(--primary-500), var(--primary-700)); color: white; margin-bottom: 20px;">
                <div style="text-align: center;">
                  <div style="font-size: 14px; opacity: 0.9; margin-bottom: 8px;">Tu ahorro mensual</div>
                  <div id="monthlySavings" style="font-size: 48px; font-weight: 800;">—</div>
                  <div id="savingsHealth" style="margin-top: 8px; font-size: 14px; opacity: 0.9;"></div>
                </div>
              </div>
              
              <div class="card animate-slideUp" style="animation-delay: 0.1s;">
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
                  <div style="text-align: center; padding: 16px; background: var(--gray-50); border-radius: var(--radius-lg);">
                    <div style="font-size: 12px; color: var(--gray-500); margin-bottom: 4px;">% de ahorro</div>
                    <div id="savingsPercentage" style="font-size: 24px; font-weight: 700; color: var(--primary-600);">—</div>
                  </div>
                  <div style="text-align: center; padding: 16px; background: var(--gray-50); border-radius: var(--radius-lg);">
                    <div style="font-size: 12px; color: var(--gray-500); margin-bottom: 4px;">Ahorro anual</div>
                    <div id="annualSavings" style="font-size: 24px; font-weight: 700; color: var(--gray-700);">—</div>
                  </div>
                </div>
              </div>
              
              <div class="card animate-slideUp" style="margin-top: 20px; animation-delay: 0.2s;">
                <h4 style="margin-bottom: 16px; display: flex; align-items: center; gap: 8px;">
                  <i data-feather="clock" style="color: var(--primary-600);"></i>
                  Tiempo estimado para comprar
                </h4>
                <div id="timeEstimate" style="color: var(--gray-600);">
                  Introduce tus gastos para ver el cálculo
                </div>
              </div>
              
              <div id="tipsSection" class="card animate-slideUp hidden" style="margin-top: 20px; animation-delay: 0.3s; border-left: 4px solid var(--warning);">
                <h4 style="margin-bottom: 12px; display: flex; align-items: center; gap: 8px;">
                  <i data-feather="lightbulb" style="color: var(--warning);"></i>
                  Consejos para mejorar
                </h4>
                <div id="tipsList"></div>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </main>
    
    ${renderFooter()}
    
    <style>
      @media (max-width: 768px) {
        .container > div > div[style*="grid-template-columns: 1fr 1fr"] {
          grid-template-columns: 1fr !important;
        }
      }

      .page-background {
        background: 
          radial-gradient(circle at 20px 20px, var(--primary-100) 2px, transparent 0),
          linear-gradient(135deg, var(--primary-50) 0%, white 100%);
        background-size: 40px 40px, 100% 100%;
        background-attachment: fixed;
      }
    </style>
  `;

  initExpensesCalculator(profile);
}

function initExpensesCalculator(profile) {
  const form = document.getElementById('expensesForm');

  // Auto-calculate on any input change
  const inputs = form.querySelectorAll('input');
  inputs.forEach(input => {
    input.addEventListener('input', () => calculateAndDisplay(profile));
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    calculateAndDisplay(profile);

    // Gather all values
    const income = parseFloat(document.getElementById('income').value) || 0;
    const rent = parseFloat(document.getElementById('rent').value) || 0;
    const fixed = parseFloat(document.getElementById('fixed').value) || 0;
    const variable = parseFloat(document.getElementById('variable').value) || 0;

    // Save expenses to localStorage
    storage.setExpenses({ rent, fixed, variable, updatedAt: new Date().toISOString() });

    // Save income to profile in localStorage (keeps it in sync)
    const currentProfile = storage.getProfile();
    if (currentProfile && currentProfile.monthlyIncome !== income) {
      currentProfile.monthlyIncome = income;
      storage.setProfile(currentProfile);
    }

    // Show "saving" state
    const btn = form.querySelector('button[type="submit"]');
    const originalText = btn.innerHTML;
    btn.innerHTML = '<div class="spinner" style="width: 18px; height: 18px; border-width: 2px;"></div> Guardando...';
    btn.disabled = true;

    // Save to backend in parallel
    try {
      const promises = [];

      // Save expenses to backend
      promises.push(
        expensesApi.saveExpenses({
          rent,
          fixedExpenses: fixed,
          variableExpenses: variable
        })
      );

      // Save income to backend profile
      promises.push(
        profileApi.updateProfile({ monthlyIncome: income })
      );

      await Promise.all(promises);
    } catch (err) {
      console.warn('Could not save to backend:', err);
    }

    // Show confirmation
    btn.disabled = false;
    btn.innerHTML = '<i data-feather="check"></i> ¡Guardado!';
    btn.style.background = 'var(--success)';

    setTimeout(() => {
      btn.innerHTML = originalText;
      btn.style.background = '';
      if (typeof feather !== 'undefined') feather.replace();
    }, 2000);
  });

  // Initial calculation if there's saved data
  const savedExpenses = storage.getExpenses();
  if (savedExpenses) {
    calculateAndDisplay(profile);
  }
}

function calculateAndDisplay(profile) {
  const income = parseFloat(document.getElementById('income').value) || 0;
  const rent = parseFloat(document.getElementById('rent').value) || 0;
  const fixed = parseFloat(document.getElementById('fixed').value) || 0;
  const variable = parseFloat(document.getElementById('variable').value) || 0;

  const result = calculateMonthlySavings(income, { rent, fixed, variable });
  const health = getSavingsHealth(result.savingsPercentage);

  // Update displays
  document.getElementById('monthlySavings').textContent = formatCurrency(result.monthlySavings);
  document.getElementById('savingsPercentage').textContent = formatPercentage(result.savingsPercentage);
  document.getElementById('annualSavings').textContent = formatCurrency(result.annualSavings);

  const healthEl = document.getElementById('savingsHealth');
  healthEl.textContent = `${health.label} - ${result.savingsPercentage >= 20 ? 'Recomendado: 20%+' : 'Objetivo: llegar al 20%'}`;

  // Time estimate
  const homeGoal = storage.getHomeGoal();
  const targetProperty = homeGoal.price;
  const downPaymentPct = (100 - homeGoal.mortgagePercentage) / 100;
  const totalNeeded = targetProperty * (downPaymentPct + 0.12);
  const currentSavings = Number(profile.currentSavings) || 0;
  const remaining = Math.max(0, totalNeeded - currentSavings);

  const timeEstimateEl = document.getElementById('timeEstimate');
  if (result.monthlySavings > 0) {
    const months = Math.ceil(remaining / result.monthlySavings);
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;

    timeEstimateEl.innerHTML = `
      <div style="background: var(--gray-50); padding: 16px; border-radius: var(--radius-lg); text-align: center;">
        <div style="font-size: 12px; color: var(--gray-500); margin-bottom: 4px;">Para una vivienda de ${formatCurrency(targetProperty)}</div>
        <div style="font-size: 32px; font-weight: 700; color: var(--primary-600);">
          ${years > 0 ? `${years} años` : ''} ${remainingMonths > 0 ? `${remainingMonths} meses` : ''}
        </div>
        <div style="font-size: 12px; color: var(--gray-500); margin-top: 4px;">
          Necesitas ${formatCurrency(totalNeeded)} (tienes ${formatCurrency(currentSavings)})
        </div>
      </div>
    `;
  } else if (result.monthlySavings < 0) {
    timeEstimateEl.innerHTML = `
      <div class="alert alert-error">
        <i data-feather="alert-circle"></i>
        <div>Tus gastos superan tus ingresos. Revisa tu presupuesto.</div>
      </div>
    `;
  } else {
    timeEstimateEl.textContent = 'Con ahorro 0, no podrás alcanzar tu objetivo.';
  }

  // Tips section
  const tipsSection = document.getElementById('tipsSection');
  const tipsList = document.getElementById('tipsList');

  if (result.savingsPercentage < 20) {
    tipsSection.classList.remove('hidden');

    const tips = [];
    if (rent / income > 0.35) {
      tips.push('Tu alquiler supera el 35% de tus ingresos. Considera opciones más económicas.');
    }
    if (variable > fixed) {
      tips.push('Tus gastos variables son altos. Revisa suscripciones y gastos de ocio.');
    }
    if (result.savingsPercentage < 10) {
      tips.push('Intenta aplicar la regla 50/30/20: 50% necesidades, 30% deseos, 20% ahorro.');
    }
    tips.push('Revisa la sección de Ahorro para más consejos personalizados.');

    tipsList.innerHTML = tips.map(tip => `
      <div style="display: flex; align-items: flex-start; gap: 8px; padding: 8px 0; border-bottom: 1px solid var(--gray-100);">
        <span style="color: var(--warning);">→</span>
        <span style="font-size: 14px; color: var(--gray-600);">${tip}</span>
      </div>
    `).join('');
  } else {
    tipsSection.classList.add('hidden');
  }

  // Reinitialize icons
  if (typeof feather !== 'undefined') feather.replace();
}
